import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import PsychologyIcon from '@mui/icons-material/Psychology';
import toast from 'react-hot-toast';

import { getSiteFramingAiErrorMessage, siteFramingAiApi } from '../../services/apiClient';
import { ConfirmDialog } from '../common/ConfirmDialog';

/** Mantenuti in sincronia con i limiti lato backend (document_extraction.py). */
const MAX_FILES = 2;
const MAX_PAGES_PER_FILE = 10;
const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024; // 15 MB
const ACCEPTED_EXTENSIONS = ['.pdf', '.docx'];

type AiMode = 'online' | 'documents';
type Phase = 'idle' | 'uploading' | 'processing';

export interface SiteFramingAiDialogProps {
  open: boolean;
  diagnosisId?: string;
  /** Testo attualmente presente nella textbox: se non vuoto, viene chiesta conferma prima di sovrascriverlo. */
  currentText?: string;
  onClose: () => void;
  /** Chiamato con il testo generato dall'AI, al termine di una generazione riuscita. */
  onGenerated: (text: string) => void;
}

function validateFile(file: File): string | null {
  const lowerName = file.name.toLowerCase();

  if (lowerName.endsWith('.doc')) {
    return `${file.name}: il formato .doc (Word 97-2003) non è supportato, salva il file in .docx`;
  }
  if (!ACCEPTED_EXTENSIONS.some(ext => lowerName.endsWith(ext))) {
    return `${file.name}: formato non supportato, sono accettati solo PDF e Word (.docx)`;
  }
  if (file.size <= 0) {
    return `${file.name}: il file è vuoto`;
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `${file.name}: supera la dimensione massima di ${Math.round(MAX_FILE_SIZE_BYTES / (1024 * 1024))} MB`;
  }
  return null;
}

export const SiteFramingAiDialog: React.FC<SiteFramingAiDialogProps> = ({
  open,
  diagnosisId,
  currentText,
  onClose,
  onGenerated,
}) => {
  const [mode, setMode] = React.useState<AiMode>('online');
  const [files, setFiles] = React.useState<File[]>([]);
  const [fileAlert, setFileAlert] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [phase, setPhase] = React.useState<Phase>('idle');
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = React.useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  // Reset dello stato interno ogni volta che il modal viene riaperto.
  React.useEffect(() => {
    if (open) {
      setMode('online');
      setFiles([]);
      setFileAlert(null);
      setLoading(false);
      setPhase('idle');
      setUploadProgress(0);
      setErrorMessage(null);
      setConfirmOverwriteOpen(false);
    }
  }, [open]);

  // Annulla la richiesta in corso se il componente viene smontato o il modal chiuso.
  React.useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const handleFilesSelected = (selected: FileList | null) => {
    if (!selected || selected.length === 0) return;

    const incoming = Array.from(selected);
    const combined = [...files, ...incoming];

    if (combined.length > MAX_FILES) {
      setFileAlert(`Puoi caricare al massimo ${MAX_FILES} file`);
      return;
    }

    for (const file of incoming) {
      const error = validateFile(file);
      if (error) {
        setFileAlert(error);
        return;
      }
    }

    setFileAlert(null);
    setFiles(combined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setFileAlert(null);
  };

  const handleModeChange = (newMode: AiMode) => {
    setMode(newMode);
    setErrorMessage(null);
    if (newMode === 'online') {
      setFiles([]);
      setFileAlert(null);
    }
  };

  const runGeneration = React.useCallback(async () => {
    if (!diagnosisId) {
      toast.error('Salva prima i dati della diagnosi');
      return;
    }

    setErrorMessage(null);
    setLoading(true);
    setUploadProgress(0);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      let text: string;
      if (mode === 'online') {
        setPhase('processing');
        text = await siteFramingAiApi.generateOnline(diagnosisId, controller.signal);
      } else {
        setPhase('uploading');
        text = await siteFramingAiApi.generateFromDocuments(diagnosisId, files, {
          signal: controller.signal,
          onUploadProgress: percent => {
            setUploadProgress(percent);
            if (percent >= 100) {
              setPhase('processing');
            }
          },
        });
      }

      if (!text || !text.trim()) {
        setErrorMessage('Il modello AI ha restituito un testo vuoto. Riprova.');
        return;
      }

      onGenerated(text);
      toast.success('Inquadramento sito generato con successo');
      onClose();
    } catch (error: unknown) {
      if (controller.signal.aborted) {
        // Annullamento esplicito dell'utente: nessun messaggio di errore.
        return;
      }
      setErrorMessage(
        getSiteFramingAiErrorMessage(error, 'Generazione AI non riuscita: riprova più tardi')
      );
    } finally {
      abortControllerRef.current = null;
      setLoading(false);
      setPhase('idle');
    }
  }, [diagnosisId, mode, files, onGenerated, onClose]);

  const handleStartClick = () => {
    if (mode === 'documents' && files.length === 0) {
      toast.error('Carica almeno un documento PDF o Word (.docx)');
      return;
    }
    if ((currentText ?? '').trim()) {
      setConfirmOverwriteOpen(true);
      return;
    }
    void runGeneration();
  };

  const handleConfirmOverwrite = () => {
    setConfirmOverwriteOpen(false);
    void runGeneration();
  };

  const handleCancelOrClose = () => {
    if (loading) {
      abortControllerRef.current?.abort();
      setLoading(false);
      setPhase('idle');
      return;
    }
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          // Il modal non si chiude cliccando fuori o con Esc mentre è in corso una richiesta.
          if (loading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
          onClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PsychologyIcon color="primary" />
          <Box sx={{ flexGrow: 1 }}>Chiedi all'AI — Inquadramento sito</Box>
          <IconButton onClick={handleCancelOrClose} size="small" disabled={loading && phase === 'uploading'}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <FormControl component="fieldset" disabled={loading}>
              <FormLabel component="legend" sx={{ fontSize: '0.8rem' }}>
                Fonte delle informazioni
              </FormLabel>
              <RadioGroup
                value={mode}
                onChange={e => handleModeChange(e.target.value as AiMode)}
              >
                <FormControlLabel value="online" control={<Radio />} label="Ricerca online" />
                <FormControlLabel
                  value="documents"
                  control={<Radio />}
                  label="Carica documenti (nessuna ricerca online)"
                />
              </RadioGroup>
            </FormControl>

            {mode === 'documents' && (
              <Stack spacing={1.5}>
                <Alert severity="info" variant="outlined">
                  Carica al massimo {MAX_FILES} file (PDF o Word .docx), ciascuno con massimo{' '}
                  {MAX_PAGES_PER_FILE} pagine scritte. Il testo deve essere selezionabile: i PDF
                  ottenuti da scansione senza riconoscimento del testo (OCR) non sono supportati.
                </Alert>

                {fileAlert && <Alert severity="error">{fileAlert}</Alert>}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx"
                  multiple
                  style={{ display: 'none' }}
                  onChange={e => handleFilesSelected(e.target.files)}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || files.length >= MAX_FILES}
                >
                  Carica file
                </Button>

                {files.length > 0 && (
                  <Stack spacing={0.5}>
                    {files.map((file, index) => (
                      <Box
                        key={`${file.name}-${index}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 1,
                          py: 0.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ overflow: 'hidden' }}>
                          <Chip
                            size="small"
                            label={file.name.toLowerCase().endsWith('.pdf') ? 'PDF' : 'DOCX'}
                          />
                          <Typography variant="body2" noWrap title={file.name}>
                            {file.name}
                          </Typography>
                        </Stack>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFile(index)}
                          disabled={loading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Stack>
            )}

            {mode === 'online' && (
              <Alert severity="info" variant="outlined">
                Verranno usati i dati aziendali già salvati (ragione sociale, ATECO, indirizzo) per
                cercare informazioni pubbliche aggiornate sullo stabilimento.
              </Alert>
            )}

            {loading && (
              <Stack spacing={1}>
                {phase === 'uploading' ? (
                  <>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography variant="caption" color="text.secondary">
                      Caricamento documenti… {uploadProgress}%
                    </Typography>
                  </>
                ) : (
                  <>
                    <LinearProgress variant="indeterminate" />
                    <Typography variant="caption" color="text.secondary">
                      L'AI sta elaborando l'inquadramento del sito, attendere…
                    </Typography>
                  </>
                )}
              </Stack>
            )}

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelOrClose} color="inherit">
            {loading ? 'Annulla richiesta' : 'Chiudi'}
          </Button>
          <Button
            variant="contained"
            onClick={handleStartClick}
            disabled={loading || !diagnosisId || (mode === 'documents' && files.length === 0)}
          >
            Inizia ricerca
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmOverwriteOpen}
        title="Sovrascrivere il contenuto esistente?"
        content="Nella textbox 'Inquadramento sito' è già presente del testo: proseguendo verrà sovrascritto con il testo generato dall'AI. Continuare?"
        confirmText="Sovrascrivi e procedi"
        cancelText="Annulla"
        onCancel={() => setConfirmOverwriteOpen(false)}
        onConfirm={handleConfirmOverwrite}
      />
    </>
  );
};
